import React from 'react';

/**
 * Hierarchical staff layout:
 *   - First member (typically the manager) sits centered on the top row.
 *   - Remaining members are arranged in a horizontal row below.
 * Uses the same visual card style as PeopleGrid for consistency.
 */
const StaffCard = ({ p }) => (
  <div className="group relative w-full" data-testid="staff-grid-item">
    <div className="relative rounded-3xl bg-gradient-to-br from-[#D4AF37] via-[#1565C0]/40 to-[#0A1628] p-[2px] shadow-md group-hover:shadow-2xl transition-shadow duration-300">
      <div className="relative rounded-[1.4rem] overflow-hidden bg-white">
        <div
          className="aspect-square w-full bg-gradient-to-br from-[#3B82F6] via-[#1E40AF] to-[#0A2E6F] transition-transform duration-500 group-hover:scale-[1.03]"
          style={
            p.image
              ? {
                  backgroundImage: `url(${p.image})`,
                  backgroundSize: p.image_size || 'contain',
                  backgroundPosition: p.image_pos || 'center bottom',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#1E40AF',
                }
              : undefined
          }
        >
          {!p.image && (
            <div className="w-full h-full flex items-center justify-center text-white text-4xl font-black">
              {(p.name || '?').trim().slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="relative bg-white px-3 py-3 border-t border-gray-100">
          <div className="font-bold text-sm md:text-[15px] text-[#0A1628] leading-tight break-words group-hover:text-[#1565C0] transition-colors">
            {p.name}
          </div>
          {p.role && (
            <div className="mt-1 flex items-start gap-1.5">
              <span className="block w-4 h-px bg-[#D4AF37] mt-2 shrink-0"></span>
              <span className="text-[10px] md:text-[11px] text-[#1565C0] font-black uppercase tracking-[0.15em] break-words leading-tight">
                {p.role}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

export const StaffTeamGrid = ({ people = [] }) => {
  if (!Array.isArray(people) || people.length === 0) return null;
  const [top, ...rest] = people;

  return (
    <div className="mt-6 flex flex-col items-center gap-8" data-testid="staff-team-grid">
      {/* Top row: single centered card (typically the manager) */}
      <div className="w-full max-w-[220px]">
        <StaffCard p={top} />
      </div>

      {/* Bottom row: 5-column grid at desktop */}
      {rest.length > 0 && (
        <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6">
          {rest.map((p) => (
            <StaffCard key={p.id || p.name} p={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffTeamGrid;
